export const privacy = {
    en: {
        metaTitle: 'Privacy Policy',
        metaDescription: 'What data TripLinkHub collects, how cookies and analytics work, what happens when you follow an affiliate link, and what rights you have.',
        heroLabel: 'Legal',
        heroTitle: 'Privacy Policy',
        lastUpdated: 'June 12, 2026',
        lead: 'TripLinkHub is an editorial travel guide. The site works without registration or accounts, so we collect very little data. This page explains exactly what we process and why.',
        note: {
            title: 'In short.',
            text: 'We do not sell your data and do not collect anything we do not need. Analytics only runs after you accept it in the cookie banner, and all purchases happen on partner websites — we never see your payment details.',
        },
        sectionLabel: (n) => `Section ${n}`,
        sections: [
            {
                title: 'General Provisions',
                paragraphs: [
                    'This Policy describes what data the TripLinkHub website (triplinkhub.com — “we”, “the site”) collects when you use it, how that data is processed, and what rights you have. The site is operated by the TripLinkHub team; you can reach us via the contact page.',
                    'By using the site you agree to the terms of this Policy. If you do not agree, please stop using the site.',
                ],
            },
            {
                title: 'What Data We Collect',
                paragraphs: ['The site has no registration or user accounts, so the data we process is minimal:'],
                bullets: [
                    { title: 'Technical data', text: 'IP address, device and browser type, pages visited and visit time. These are recorded automatically in the hosting provider’s server logs and are needed for security and stable operation of the site.' },
                    { title: 'Analytics data', text: 'anonymised visit statistics (Google Analytics 4): which pages are viewed, from which devices and countries. Collected only after your consent.' },
                    { title: 'Cookies and localStorage', text: 'your choice in the consent banner, preferred price currency and other interface settings stored in your browser.' },
                    { title: 'Contact form data', text: 'name, email, subject and message text — only if you submit them yourself via the form on the contact page. We use them solely to reply to you.' },
                ],
            },
            {
                title: 'Cookies and Consent',
                paragraphs: [
                    'On your first visit the site shows a consent banner. Until you decide, analytics cookies are disabled (Google Consent Mode): “Accept” enables analytics, “Essential only” keeps just the technically necessary settings.',
                    'You can withdraw consent at any time: clear the site data in your browser settings — the banner will appear again on your next visit, and analytics will stay off until you accept it.',
                ],
            },
            {
                title: 'Google Analytics',
                paragraphs: [],
                richParagraphs: [
                    { before: 'We use Google Analytics 4 in Consent Mode to understand how the site is used: no data is collected before consent, and after consent the statistics are anonymised. This helps us see which guides are useful to readers. Learn more in the ', linkText: 'Google Privacy Policy', linkHref: 'https://policies.google.com/privacy', after: '.' },
                ],
            },
            {
                title: 'Affiliate Links',
                paragraphs: [
                    'TripLinkHub is an affiliate project: some links on the site are affiliate links. If you follow one and book or buy a service (a tour, eSIM, insurance, accommodation or transfer), we may earn a commission from the partner — for example Booking.com, Trip.com, Viator, Tripster, GetYourGuide, Airalo and others, including via the Travelpayouts affiliate network. The price for you does not change.',
                    'An affiliate link carries a technical tag (subID) that identifies the section of our site the click came from — it contains no personal data. Clicks are counted as anonymous statistics (section, provider, country), with no link to your identity.',
                    'After the click you land on the partner’s website: it may set its own cookies and processes your data under its own privacy policy. The purchase is made with the partner — we never see or store your payment details.',
                ],
            },
            {
                title: 'Sharing Data with Third Parties',
                paragraphs: [
                    'We do not sell or share your data with third parties for marketing purposes. Technical data is accessible only to the service providers that keep the site running (hosting, analytics), strictly to the extent needed for their function. Data may be disclosed only where the law requires it.',
                ],
            },
            {
                title: 'Storage and Security',
                paragraphs: [
                    'The connection to the site is protected with HTTPS. We keep data no longer than needed for the purposes above: server logs and analytics are rotated within the standard retention periods of the respective services, and contact-form correspondence is kept only while your request is active.',
                ],
            },
            {
                title: 'Your Rights',
                paragraphs: ['You have the right to:'],
                bullets: [
                    { text: 'Access the personal data we hold about you.' },
                    { text: 'Correct inaccurate or incomplete data.' },
                    { text: 'Request deletion of your personal data.' },
                    { text: 'Withdraw previously given consent at any time.' },
                    { text: 'Lodge a complaint with the data-protection authority of your place of residence.' },
                ],
                contactText: 'To exercise these rights, ',
                contactLinkText: 'contact us',
                contactAfter: '.',
            },
            {
                title: 'Children',
                paragraphs: [
                    'The site is not directed at children under 16, and we do not knowingly collect their data.',
                ],
            },
            {
                title: 'Changes to This Policy',
                paragraphs: [
                    'We may update this Policy from time to time. Significant changes are reflected in the “Updated” date at the top of the page; the current version is always available at this address.',
                ],
            },
            {
                title: 'Contact',
                paragraphs: [],
                contactText: 'If you have questions about this Policy or how your data is handled, ',
                contactLinkText: 'contact us',
                contactAfter: ' via the form on the contact page.',
            },
        ],
    },
    ru: {
        metaTitle: 'Политика конфиденциальности',
        metaDescription: 'Какие данные собирает TripLinkHub, как работают cookies и аналитика, что происходит при переходе по партнёрской ссылке и какие права есть у пользователя.',
        heroLabel: 'Документы',
        heroTitle: 'Политика конфиденциальности',
        lastUpdated: '12 июня 2026',
        lead: 'TripLinkHub — редакционный гайд для самостоятельных путешественников. Сайт работает без регистрации и личных кабинетов, поэтому данных мы собираем очень мало. На этой странице — что именно мы обрабатываем и зачем.',
        note: {
            title: 'Коротко.',
            text: 'Мы не продаём ваши данные и не собираем лишнего. Аналитика включается только после вашего согласия в баннере cookies, а все покупки совершаются на сайтах партнёров — ваши платёжные данные мы не видим.',
        },
        sectionLabel: (n) => `Раздел ${n}`,
        sections: [
            {
                title: 'Общие положения',
                paragraphs: [
                    'Настоящая Политика описывает, какие данные собирает сайт TripLinkHub (triplinkhub.com — далее «мы», «сайт») при его использовании, как они обрабатываются и какие права есть у вас. Сайтом управляет команда TripLinkHub; связаться с нами можно через страницу контактов.',
                    'Используя сайт, вы соглашаетесь с условиями настоящей Политики. Если вы с ними не согласны, пожалуйста, прекратите использование сайта.',
                ],
            },
            {
                title: 'Какие данные мы собираем',
                paragraphs: ['На сайте нет регистрации и личных кабинетов, поэтому объём обрабатываемых данных минимален:'],
                bullets: [
                    { title: 'Технические данные', text: 'IP-адрес, тип устройства и браузера, открытые страницы и время визита. Фиксируются автоматически в журналах хостинг-провайдера и нужны для безопасности и стабильной работы сайта.' },
                    { title: 'Аналитические данные', text: 'обезличенная статистика посещений (Google Analytics 4): какие страницы открывают, с каких устройств и из каких стран. Собирается только после вашего согласия.' },
                    { title: 'Cookies и localStorage', text: 'ваш выбор в баннере согласия, предпочитаемая валюта цен и другие настройки интерфейса, сохраняемые в вашем браузере.' },
                    { title: 'Данные формы обратной связи', text: 'имя, email, тема и текст сообщения — только если вы сами отправили их через форму на странице контактов. Используем их исключительно для ответа вам.' },
                ],
            },
            {
                title: 'Cookies и согласие',
                paragraphs: [
                    'При первом визите сайт показывает баннер согласия. Пока вы не приняли решение, аналитические cookies отключены (Google Consent Mode): кнопка «Принять» включает аналитику, «Только необходимые» оставляет лишь технически необходимые настройки.',
                    'Отозвать согласие можно в любой момент: очистите данные сайта в настройках браузера — при следующем визите баннер появится снова, а аналитика останется выключенной, пока вы не согласитесь.',
                ],
            },
            {
                title: 'Google Analytics',
                paragraphs: [],
                richParagraphs: [
                    { before: 'Для анализа посещаемости мы используем Google Analytics 4 в режиме Consent Mode: до согласия данные не собираются, после — собираются в обезличенном виде. Эта статистика помогает понимать, какие материалы полезны читателям. Подробнее — в ', linkText: 'Политике конфиденциальности Google', linkHref: 'https://policies.google.com/privacy', after: '.' },
                ],
            },
            {
                title: 'Партнёрские ссылки',
                paragraphs: [
                    'TripLinkHub — аффилиатный проект: часть ссылок на сайте партнёрские. Если вы перейдёте по такой ссылке и оформите услугу (тур, eSIM, страховку, жильё или трансфер), мы можем получить комиссию от партнёра — например, Booking.com, Trip.com, Viator, Tripster, GetYourGuide, Airalo и других, в том числе через партнёрскую сеть Travelpayouts. Цена для вас при этом не меняется.',
                    'К партнёрской ссылке добавляется служебная метка (subID) — она показывает, из какого раздела сайта совершён переход, и не содержит ваших личных данных. Клики учитываются в обезличенной статистике (раздел, провайдер, страна) без привязки к личности.',
                    'После перехода вы попадаете на сайт партнёра: он может устанавливать собственные cookies и обрабатывает ваши данные по своей политике конфиденциальности. Покупка оформляется у партнёра — мы не видим и не храним ваши платёжные данные.',
                ],
            },
            {
                title: 'Передача данных третьим лицам',
                paragraphs: [
                    'Мы не продаём и не передаём ваши данные третьим лицам в маркетинговых целях. Доступ к техническим данным имеют только сервис-провайдеры, обеспечивающие работу сайта (хостинг, аналитика), — строго в объёме, необходимом для их функций. Раскрытие данных возможно только в случаях, предусмотренных законом.',
                ],
            },
            {
                title: 'Хранение и защита данных',
                paragraphs: [
                    'Соединение с сайтом защищено протоколом HTTPS. Мы храним данные не дольше, чем это необходимо для целей выше: журналы сервера и аналитика очищаются в стандартные сроки соответствующих сервисов, переписка по форме обратной связи хранится, пока актуален ваш запрос.',
                ],
            },
            {
                title: 'Ваши права',
                paragraphs: ['Вы имеете право:'],
                bullets: [
                    { text: 'Получить доступ к персональным данным, которые мы о вас храним.' },
                    { text: 'Исправить неточные или неполные данные.' },
                    { text: 'Запросить удаление ваших персональных данных.' },
                    { text: 'В любой момент отозвать ранее данное согласие.' },
                    { text: 'Подать жалобу в надзорный орган по защите данных по месту вашего проживания.' },
                ],
                contactText: 'Чтобы воспользоваться этими правами, ',
                contactLinkText: 'свяжитесь с нами',
                contactAfter: '.',
            },
            {
                title: 'Дети',
                paragraphs: [
                    'Сайт не предназначен для детей младше 16 лет, и мы сознательно не собираем их данные.',
                ],
            },
            {
                title: 'Изменения политики',
                paragraphs: [
                    'Мы можем время от времени обновлять эту Политику. Существенные изменения отражаются в дате «Обновлено» вверху страницы; актуальная версия всегда доступна по этому адресу.',
                ],
            },
            {
                title: 'Контакты',
                paragraphs: [],
                contactText: 'Вопросы по этой Политике и обработке данных направляйте через ',
                contactLinkText: 'страницу контактов',
                contactAfter: '.',
            },
        ],
    },
};

export const disclaimer = {
    en: {
        metaTitle: 'Disclaimer',
        metaDescription: 'TripLinkHub is an editorial travel guide: we are not a seller or tour operator. This page explains the limits of our responsibility and how affiliate links work.',
        heroLabel: 'Legal',
        heroTitle: 'Disclaimer',
        lastUpdated: 'June 12, 2026',
        lead: 'TripLinkHub is an information project for travellers. We strive for accuracy, but travel conditions, prices and availability change constantly. This page explains the limits of our responsibility.',
        note: {
            title: 'In short.',
            text: 'Everything on this site is reference information, not an offer. Before travelling or paying, always verify the terms on the official website of the service provider — the contract is always between you and them.',
        },
        sectionLabel: (n) => `Section ${n}`,
        sections: [
            {
                title: 'General Provisions',
                paragraphs: [
                    'TripLinkHub is an informational website that helps travellers compare and choose services — tours, eSIMs, VPNs, insurance, transport and accommodation — offered by third-party companies and providers.',
                    'We are not the organiser, seller or provider of these services. All information published on the site is for reference only and does not constitute a public offer.',
                ],
            },
            {
                title: 'Affiliate Links',
                paragraphs: [],
                richParagraphs: [
                    { before: 'Some links on the site are affiliate links: if you follow one and make a booking or purchase, we may earn a commission from the partner. The price for you does not change, and the commission does not make us a party to the transaction — the contract is always concluded directly between you and the service provider. More details in our ', linkText: 'Privacy Policy', linkHref: '/privacy', internal: true, after: '.' },
                ],
            },
            {
                title: 'Limitation of Liability',
                paragraphs: ['TripLinkHub is not liable for:'],
                bullets: [
                    { title: 'Accuracy of information', text: 'the accuracy and currency of information provided by third-party suppliers.' },
                    { title: 'Unmet expectations', text: 'whether services or products meet the user’s expectations.' },
                    { title: 'Third-party actions', text: 'actions or omissions of third parties, including delays, cancellations, technical failures or any other issues arising from the use of products and services listed on the site.' },
                    { title: 'Financial losses', text: 'direct or indirect losses incurred as a result of purchasing or using products and services referenced on the site.' },
                ],
            },
            {
                title: 'Prices and Conditions',
                paragraphs: [
                    'Prices, rates and service conditions shown on the site are indicative and may change without notice. Prices are displayed in the partner’s currency; any conversion to other currencies shown on the site is approximate and may differ from your bank’s exchange rate.',
                    'Always verify the current price and terms directly with the service provider before booking or paying.',
                ],
            },
            {
                title: 'Visa and Entry Information',
                paragraphs: [
                    'Information about visa requirements, entry conditions, border control rules and other travel formalities is provided for reference only and may not reflect the situation at the time of your trip.',
                    'You are responsible for verifying current requirements through official sources — embassies, consulates and government authorities of the destination country — before planning or undertaking a trip. TripLinkHub accepts no liability for consequences arising from the use of outdated or inaccurate entry information.',
                ],
            },
            {
                title: 'Not Professional Advice',
                paragraphs: [
                    'Materials on the site — including reviews of insurance products, eSIMs, VPNs and trip-planning tips — are editorial content and do not constitute legal, medical, financial or other professional advice. For decisions that affect your health, finances or legal status, consult a qualified professional.',
                ],
            },
            {
                title: 'External Links',
                paragraphs: [
                    'The site contains links to third-party websites and services. TripLinkHub does not control their content and is not responsible for their privacy policies, terms of use, quality of services or any other content published on them. You follow external links at your own risk.',
                ],
            },
            {
                title: 'User Responsibility',
                paragraphs: [
                    'You bear full responsibility for any decision to purchase services or products referenced on TripLinkHub, and you should review the terms, refund policies, service rules and any other conditions set by the third-party provider before paying.',
                ],
            },
            {
                title: 'Intellectual Property',
                paragraphs: [
                    'The site’s materials — texts, guides, collections and design — belong to TripLinkHub and may not be copied or republished without permission, except for short quotations with a link to the source. Logos and trademarks of the services mentioned belong to their respective owners and are used for informational purposes only.',
                ],
            },
            {
                title: 'Acceptance of Terms',
                paragraphs: [
                    'By using TripLinkHub you confirm that you have read this Disclaimer and accept its terms in full. We may update it — the current version is always available on this page.',
                ],
            },
        ],
    },
    ru: {
        metaTitle: 'Отказ от ответственности',
        metaDescription: 'TripLinkHub — редакционный гайд о путешествиях: мы не продавец и не туроператор. На этой странице — границы нашей ответственности и как устроены партнёрские ссылки.',
        heroLabel: 'Документы',
        heroTitle: 'Отказ от ответственности',
        lastUpdated: '12 июня 2026',
        lead: 'TripLinkHub — информационный проект для путешественников. Мы стремимся к точности, но условия поездок, цены и доступность услуг постоянно меняются. Эта страница объясняет границы нашей ответственности.',
        note: {
            title: 'Главное.',
            text: 'Всё на этом сайте — справочная информация, а не оферта. Перед поездкой и оплатой всегда проверяйте условия на официальном сайте поставщика услуги: договор вы заключаете напрямую с ним.',
        },
        sectionLabel: (n) => `Раздел ${n}`,
        sections: [
            {
                title: 'Общие положения',
                paragraphs: [
                    'TripLinkHub — информационный сайт, который помогает путешественникам сравнивать и выбирать услуги: туры, eSIM, VPN, страховки, транспорт и жильё, предлагаемые сторонними компаниями и провайдерами.',
                    'Мы не являемся организатором, продавцом или исполнителем этих услуг. Вся информация на сайте носит исключительно справочный характер и не является публичной офертой.',
                ],
            },
            {
                title: 'Партнёрские ссылки',
                paragraphs: [],
                richParagraphs: [
                    { before: 'Часть ссылок на сайте — партнёрские: если вы перейдёте по такой ссылке и оформите бронирование или покупку, мы можем получить комиссию от партнёра. Цена для вас не меняется, а комиссия не делает нас стороной сделки — договор всегда заключается напрямую между вами и поставщиком услуги. Подробнее — в ', linkText: 'Политике конфиденциальности', linkHref: '/privacy', internal: true, after: '.' },
                ],
            },
            {
                title: 'Ограничение ответственности',
                paragraphs: ['TripLinkHub не несёт ответственности за:'],
                bullets: [
                    { title: 'Качество информации', text: 'точность и актуальность сведений, предоставленных сторонними поставщиками.' },
                    { title: 'Соответствие ожиданиям', text: 'соответствие услуг и продуктов ожиданиям пользователя.' },
                    { title: 'Действия третьих лиц', text: 'действия или бездействие третьих лиц, в том числе задержки, отмены, технические сбои и иные проблемы, возникающие при использовании продуктов и услуг, информация о которых представлена на сайте.' },
                    { title: 'Финансовые потери', text: 'прямые или косвенные убытки, понесённые в результате приобретения или использования продуктов и услуг, упомянутых на сайте.' },
                ],
            },
            {
                title: 'Цены и условия',
                paragraphs: [
                    'Цены, тарифы и условия предоставления услуг на сайте носят ориентировочный характер и могут меняться без предупреждения. Цены отображаются в валюте партнёра; пересчёт в другие валюты на сайте — приблизительный и может отличаться от курса вашего банка.',
                    'Актуальную стоимость и условия всегда проверяйте непосредственно у поставщика услуги перед бронированием или оплатой.',
                ],
            },
            {
                title: 'Визовая и въездная информация',
                paragraphs: [
                    'Информация о визовых требованиях, условиях въезда, правилах пограничного контроля и иных формальностях предоставляется исключительно в справочных целях и может не отражать положение дел на момент вашей поездки.',
                    'Перед планированием и совершением поездки вы обязаны самостоятельно проверить актуальные требования в официальных источниках — посольствах, консульствах и государственных органах страны назначения. TripLinkHub не несёт ответственности за последствия использования устаревшей или неточной въездной информации.',
                ],
            },
            {
                title: 'Не является профессиональной консультацией',
                paragraphs: [
                    'Материалы сайта — включая обзоры страховок, eSIM, VPN и советы по подготовке к поездке — являются редакционным контентом и не являются юридической, медицинской, финансовой или иной профессиональной консультацией. Для решений, влияющих на ваше здоровье, финансы или правовой статус, обращайтесь к профильным специалистам.',
                ],
            },
            {
                title: 'Внешние ссылки',
                paragraphs: [
                    'Сайт содержит ссылки на сторонние ресурсы и сервисы. TripLinkHub не контролирует их содержимое и не отвечает за их политику конфиденциальности, условия использования, качество услуг и любой иной размещённый на них контент. Переход по внешним ссылкам вы совершаете на свой риск.',
                ],
            },
            {
                title: 'Ответственность пользователя',
                paragraphs: [
                    'Вы несёте полную ответственность за любые решения о приобретении услуг и продуктов, информация о которых представлена на TripLinkHub, и перед оплатой должны самостоятельно ознакомиться с условиями, политикой возврата, правилами обслуживания и прочими требованиями стороннего поставщика.',
                ],
            },
            {
                title: 'Интеллектуальная собственность',
                paragraphs: [
                    'Материалы сайта — тексты, гайды, подборки и оформление — принадлежат TripLinkHub; их копирование и переиздание без разрешения не допускается, за исключением коротких цитат со ссылкой на источник. Логотипы и товарные знаки упомянутых сервисов принадлежат их владельцам и используются исключительно в информационных целях.',
                ],
            },
            {
                title: 'Согласие с условиями',
                paragraphs: [
                    'Используя TripLinkHub, вы подтверждаете, что ознакомились с настоящим Отказом от ответственности и принимаете его условия в полном объёме. Мы можем обновлять документ — актуальная версия всегда доступна на этой странице.',
                ],
            },
        ],
    },
};

export const contact = {
    en: {
        metaTitle: 'Contact Us',
        metaDescription: 'Get in touch with TripLinkHub. Have questions or suggestions about travel services, eSIM, VPN, tours or accommodation? We are happy to help.',
        heroLabel: 'Contact',
        heroTitle: 'Get in Touch',
        heroSubtitle: "Have questions or suggestions? We're always happy to help and hear your feedback",
        howToReach: 'How to Reach Us',
        contactInfo: 'Contact Information',
        contactInfoSubtitle: "Choose a convenient way to reach us \u2014 we'll respond as soon as possible",
        emailDescription: 'For business inquiries and partnerships',
        telegramDescription: 'Quick replies via messenger',
        responseTime: 'We typically respond ',
        responseTimeBold: 'within 24 hours',
        blogTitle: 'Read Our Blog',
        blogSubtitle: 'Tips, guides, and answers to common questions',
        blogLink: 'Go to Blog',
        formLabel: 'Contact Form',
        formTitle: 'Send us a Message',
        successTitle: 'Message Sent!',
        successMessage: "Thank you for reaching out. We'll get back to you soon.",
        sendAnother: 'Send Another',
        nameLabel: 'Your Name',
        namePlaceholder: "What's your name?",
        emailLabel: 'Email',
        subjectLabel: 'Subject',
        subjectPlaceholder: 'What would you like to discuss?',
        messageLabel: 'Message',
        messagePlaceholder: 'Tell us more...',
        sendButton: 'Send Message',
        sendingButton: 'Sending...',
        networkError: 'Network error. Please try again later.',
        genericError: 'An error occurred while sending',
        showTelegram: true,
    },
    ru: {
        metaTitle: 'Контакты',
        metaDescription: 'Свяжитесь с TripLinkHub. Есть вопросы или предложения о туристических сервисах, eSIM, VPN, турах или проживании? Мы рады помочь.',
        heroLabel: 'Контакты',
        heroTitle: 'Связаться с нами',
        heroSubtitle: 'Есть вопросы или предложения? Мы всегда рады помочь и\u00a0услышать вашу обратную связь',
        howToReach: 'Как нас найти',
        contactInfo: 'Контактная информация',
        contactInfoSubtitle: 'Выберите удобный способ связи \u2014 мы ответим в кратчайшие сроки',
        emailDescription: 'Для деловых запросов и сотрудничества',
        telegramDescription: '',
        responseTime: '',
        responseTimeBold: '',
        blogTitle: 'Читайте наш блог',
        blogSubtitle: 'Советы, гайды и ответы на вопросы',
        blogLink: 'Перейти в блог',
        formLabel: 'Форма обратной связи',
        formTitle: 'Напишите нам',
        successTitle: 'Сообщение отправлено!',
        successMessage: 'Спасибо за ваше обращение. Мы свяжемся с вами в ближайшее время.',
        sendAnother: 'Отправить ещё',
        nameLabel: 'Ваше имя',
        namePlaceholder: 'Как вас зовут?',
        emailLabel: 'Email',
        subjectLabel: 'Тема',
        subjectPlaceholder: 'О чём хотите написать?',
        messageLabel: 'Сообщение',
        messagePlaceholder: 'Расскажите подробнее...',
        sendButton: 'Отправить сообщение',
        sendingButton: 'Отправка...',
        networkError: 'Ошибка сети. Попробуйте позже.',
        genericError: 'Произошла ошибка при отправке',
        showTelegram: false,
    },
};
